import { useState } from 'react';

interface ACQItem {
    id: number;
    text: string;
}

const ACQ_ITEMS: ACQItem[] = [
    { id: 1, text: 'Actively watching television or movies. Please do not include time where the television was only on in the background.' },
    { id: 2, text: 'Actively reading, either online (e.g., emails) or in paper-form (e.g., newspapers, letters, mail).' },
    { id: 3, text: 'Writing (of any kind, e.g., emails, stories, letters).' },
    { id: 4, text: 'Interacting with your closest confidantes (i.e., those few who you are closest to; e.g., spouse, children) either talking face-to-face, on the phone, or video calls such as via Skype. Please do not include pets.' },
    { id: 5, text: 'Interacting with people who are not your closest confidantes (i.e., any person not included in question 4) either face-to-face, on the phone, or video calls such as via Skype.' },
    { id: 6, text: 'Organizing or planning (e.g., your agenda or schedule, materials, food, other peoples’ activities).' },
    { id: 7, text: 'Solving a problem on your own (e.g., work, car, finances, research, travel).' },
    { id: 8, text: 'Actively participating in conversations or meetings that focused on solving a problem (e.g., group decisions, your or another’s personal or social problem, politics).' },
    { id: 9, text: 'Working on complex decisions (i.e., requires lots of thought, have to consider multiple factors, and there will be different consequences depending on what you choose).' },
    { id: 10, text: 'Meeting new people (i.e., where face-to-face introductions took place).' },
    { id: 11, text: 'Actively listening to information (e.g., attending a lecture, listening to talk radio at home or in the car, TV news). Please do not include time where the information was only on in the background.' },
    { id: 12, text: 'Attending community events (e.g., going to a concert or play, visiting a museum or gallery, sporting events, religious events).' },
    { id: 13, text: 'Doing a hobby you know well that does not require very much conscious attention (e.g., something that is automatic for you to do).' },
    { id: 14, text: 'Learning a new skill (e.g., language, hobby, technique, sport, cooking, instrument).' },
    { id: 15, text: 'Doing an activity that requires you create something (i.e., there are no set rules to it) (e.g., painting, acting, photography, creating music, cooking without a recipe where you create as you go).' },
    { id: 16, text: 'Playing physical or mental games against an opponent (e.g., tennis, basketball, pool, chess, Scrabble, some card games, some board games). Please do not include online computer games.' },
    { id: 17, text: 'Mental games that you play on your own where you test your memory, reasoning, or spatial abilities (include paper-form, and online games).' },
    { id: 18, text: 'Mental games that you play on your own where you test your knowledge or vocabulary (include paper-form, and online games).' },
    { id: 19, text: 'Leading, coaching, supervising, teaching, or mentoring others. This does not have to be a formal role.' },
    { id: 20, text: 'Going out shopping in person (grocery, clothes, anything). Please also include time if no purchases were made.' },
    { id: 21, text: 'Shopping online. Please also include time if no purchases were made.' },
    { id: 22, text: 'Finding or researching information using the internet, or reference books/materials.' },
    { id: 23, text: 'Doing a physical activity alongside or while interacting with other people.' },
    { id: 24, text: 'Doing things that were outside your normal daily routine.' },
    { id: 25, text: 'Doing light-intensity activity or exercise (i.e., very easy for you to do, very little increase in heart rate and breathing rate, no sweating due to activity).' },
    { id: 26, text: 'Doing medium-intensity activity or exercise (i.e., moderate increase in breathing, light sweating, but you could have pushed yourself further).' },
    { id: 27, text: 'Doing vigorous-intensity activity or exercise (i.e., breathing heavily, sweating heavily, pushing your body/heart rate to a high intensity).' },
];

const RESPONSE_OPTIONS = [
    'No time at all',
    'Some time but less than 15 min',
    '15–30 min',
    '31 min–1 h',
    '1–2 h',
    '2–4 h',
    '4–8 h',
    '8–12 h',
];

export default function ACQ() {
    const [answers, setAnswers] = useState<Record<number, number>>({});
    const [submitted, setSubmitted] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const setAnswer = (itemId: number, value: number) => {
        setAnswers(prev => ({ ...prev, [itemId]: value }));
    };

    const allAnswered = ACQ_ITEMS.every(item => answers[item.id] !== undefined);

    const handleSubmit = () => {
        if (!allAnswered) {
            setShowValidation(true);
            return;
        }
        setSubmitted(true);
    };

    const handleRetake = () => {
        setAnswers({});
        setSubmitted(false);
        setShowValidation(false);
    };

    if (submitted) {
        return (
            <>
                <div className="breadcrumb">neuroFit &gt; ACQ</div>
                <div className="title">ACQ</div>
                <div className="drop-shadow-content-box">
                    <div className="sectionTitle">Yesterday’s Responses</div>
                    <ul className="acqSummaryList">
                        {ACQ_ITEMS.map(item => (
                            <li key={item.id} className="acqSummaryItem">
                                <span className="acqSummaryItemText">{item.id}. {item.text}</span>
                                <span className="acqSummaryItemValue">{RESPONSE_OPTIONS[answers[item.id]]}</span>
                            </li>
                        ))}
                    </ul>
                    <button type="button" className="mrsRetakeButton" onClick={handleRetake}>Retake</button>
                </div>
                <p className="mrsCitation">
                    Bielak AAM. Different perspectives on measuring lifestyle engagement: a comparison of activity measures and their relation with cognitive performance in older adults. <em>Aging Neuropsychol Cogn</em>. 2017;24(4):435-452. doi:10.1080/13825585.2016.1221378
                </p>
            </>
        );
    }

    return (
        <>
            <div className="breadcrumb">neuroFit &gt; ACQ</div>
            <div className="title">ACQ</div>
            <div className="drop-shadow-content-box">
                <p className="mrsInstructions">
                    Please report the amount of time you spent <strong>yesterday</strong> (midnight to midnight) participating in activities that had each of the characteristics listed below. You may have more than one activity in mind for a question — total the time spent on activities that fit each characteristic. Some activities may count toward more than one question.
                </p>
                {ACQ_ITEMS.map(item => (
                    <fieldset
                        key={item.id}
                        className={`mrsItem ${showValidation && answers[item.id] === undefined ? 'mrsItemUnanswered' : ''}`}
                    >
                        <legend className="mrsItemText">{item.id}. {item.text}</legend>
                        <div className="mrsResponseOptions">
                            {RESPONSE_OPTIONS.map((label, value) => (
                                <label key={value} className="mrsResponseOption">
                                    <input
                                        type="radio"
                                        name={`acq-item-${item.id}`}
                                        checked={answers[item.id] === value}
                                        onChange={() => setAnswer(item.id, value)}
                                    />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                ))}
                {showValidation && !allAnswered && (
                    <p className="mrsValidationMessage">Please answer every item before submitting.</p>
                )}
                <button type="button" className="mrsSubmitButton" onClick={handleSubmit}>Submit</button>
            </div>
            <p className="mrsCitation">
                Bielak AAM. Different perspectives on measuring lifestyle engagement: a comparison of activity measures and their relation with cognitive performance in older adults. <em>Aging Neuropsychol Cogn</em>. 2017;24(4):435-452. doi:10.1080/13825585.2016.1221378
            </p>
        </>
    );
}
